import { securityIdentityPresentation } from './securityIdentityPresentation';

const TICKER_EXPOSURE_THRESHOLD = 0.1;
const FACTOR_SELECTION_LIMIT = 30;

const asFiniteNumber = (value) => (Number.isFinite(value) ? value : null);

const compareNullable = (left, right, comparePresent) => {
    const leftMissing = left == null;
    const rightMissing = right == null;

    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;
    return comparePresent(left, right);
};

const compareText = (left, right) => String(left).localeCompare(String(right));
const compareMagnitude = (left, right) => Math.abs(left) - Math.abs(right);

const withOrder = (comparison, order) => (
    comparison === 0 || order === 'asc' ? comparison : -comparison
);

const getTickerRowKey = (row) => {
    for (const candidate of [row?.cognive_id, row?.sedol, row?.ticker]) {
        if (candidate != null && String(candidate).trim()) {
            return String(candidate).trim();
        }
    }
    return '';
};

export const enrichFactorDetailRows = (positions = [], holdings = []) => {
    const holdingsById = new Map(holdings
        .filter((row) => row?.cognive_id)
        .map((row) => [String(row.cognive_id), row]));
    return positions.map((row) => {
        const holding = holdingsById.get(String(row?.cognive_id ?? ''));
        if (!holding) return row;
        return {
            ...holding,
            ...row,
            msi_listing_label: row?.msi_listing_label ?? holding.msi_listing_label,
            company_name: row?.company_name ?? holding.company_name,
            inbound_id_type: row?.inbound_id_type ?? holding.inbound_id_type,
            inbound_id_value: row?.inbound_id_value ?? holding.inbound_id_value,
            uploaded_ticker: row?.uploaded_ticker ?? holding.uploaded_ticker,
        };
    });
};

export const buildTickerExposureRows = (tickers = []) => {
    const visibleRows = tickers
        .filter((ticker) => (
            Number.isFinite(ticker?.exposure)
            && Math.abs(ticker.exposure) >= TICKER_EXPOSURE_THRESHOLD
        ));
    const totalExposure = visibleRows.reduce((sum, ticker) => sum + ticker.exposure, 0);

    return Object.freeze(visibleRows.map((ticker, sourceIndex) => {
        const nmv = asFiniteNumber(ticker.nmv);
        return Object.freeze({
            ...ticker,
            rowKey: getTickerRowKey(ticker),
            sourceIndex,
            factorVarPct: asFiniteNumber(ticker.factorVarPct),
            nmv,
            expPerNmv: nmv !== null && nmv !== 0 ? ticker.exposure / nmv : null,
            pctExp: totalExposure !== 0 ? (ticker.exposure / totalExposure) * 100 : null,
        });
    }));
};

const TICKER_NUMERIC_FIELDS = new Set([
    'exposure',
    'factorVarPct',
    'expPerNmv',
    'pctExp',
]);

export const sortTickerExposureRows = (rows, order, orderBy, identityDisplayField = 'company_name') => Object.freeze(
    [...rows].sort((left, right) => {
        let comparison;
        if (TICKER_NUMERIC_FIELDS.has(orderBy)) {
            comparison = compareNullable(
                asFiniteNumber(left[orderBy]),
                asFiniteNumber(right[orderBy]),
                compareMagnitude,
            );
            if (comparison !== 0 && left[orderBy] != null && right[orderBy] != null) {
                comparison = withOrder(comparison, order);
            }
        } else {
            const leftValue = orderBy === 'security'
                ? securityIdentityPresentation(left, identityDisplayField).value
                : left.ticker;
            const rightValue = orderBy === 'security'
                ? securityIdentityPresentation(right, identityDisplayField).value
                : right.ticker;
            comparison = withOrder(compareText(leftValue ?? '', rightValue ?? ''), order);
        }

        if (comparison !== 0) return comparison;
        const keyComparison = compareText(left.rowKey, right.rowKey);
        if (keyComparison !== 0) return keyComparison;
        return left.sourceIndex - right.sourceIndex;
    }),
);

export const selectTickerExposureRows = ({ tickers, order, orderBy, identityDisplayField }) => (
    sortTickerExposureRows(buildTickerExposureRows(tickers), order, orderBy, identityDisplayField)
);

const getFactorName = (entry) => (
    typeof entry?.factor === 'string' ? entry.factor.trim() : ''
);

const indexFactorExposures = (exposures) => {
    const indexed = new Map();
    (Array.isArray(exposures) ? exposures : []).forEach((entry) => {
        const factor = getFactorName(entry);
        const value = asFiniteNumber(entry?.factor_exposure_3std_swing);
        if (factor && value !== null) {
            indexed.set(factor, value);
        }
    });
    return indexed;
};

const buildFactorNameDigest = (factorNames) => (
    JSON.stringify([...factorNames].sort(compareText))
);

export const buildFactorExposureModel = (actualExposures = [], optimizedExposures = []) => {
    const actualByFactor = indexFactorExposures(actualExposures);
    const optimizedByFactor = indexFactorExposures(optimizedExposures);
    const rows = Object.freeze([...actualByFactor].map(([factor, actual]) => Object.freeze({
        factor,
        actual,
        optimized: optimizedByFactor.get(factor) ?? null,
    })));
    const orderedFactorNames = [...rows]
        .sort((left, right) => {
            const magnitude = compareMagnitude(right.actual, left.actual);
            return magnitude || compareText(left.factor, right.factor);
        })
        .map(({ factor }) => factor);

    return Object.freeze({
        rows,
        hasOptimized: optimizedByFactor.size > 0,
        factorNameDigest: buildFactorNameDigest(rows.map(({ factor }) => factor)),
        orderedFactorNames: Object.freeze(orderedFactorNames),
        defaultFactorNames: Object.freeze(orderedFactorNames.slice(0, FACTOR_SELECTION_LIMIT)),
    });
};

export const selectFactorChartRows = ({ rows, selectedFactors, sortKey, sortOrder }) => {
    const selected = new Set(selectedFactors);
    const chartRows = rows
        .filter(({ factor }) => selected.has(factor))
        .map(({ factor, actual, optimized }) => Object.freeze({
            factor,
            value: Math.round(actual),
            optValue: optimized == null ? null : Math.round(optimized),
        }))
        .sort((left, right) => {
            if (sortKey === 'category') {
                return withOrder(compareText(left.factor, right.factor), sortOrder);
            }
            const field = sortKey === 'optimized' ? 'optValue' : 'value';
            const comparison = compareNullable(left[field], right[field], compareMagnitude);
            if (comparison !== 0 && left[field] != null && right[field] != null) {
                return withOrder(comparison, sortOrder);
            }
            return comparison || compareText(left.factor, right.factor);
        });
    return Object.freeze(chartRows);
};

const indexGroupValues = (rows) => {
    const indexed = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const category = typeof row?.category === 'string' ? row.category.trim() : '';
        if (category) indexed.set(category, asFiniteNumber(row.value));
    });
    return indexed;
};

export const selectGroupChartRows = ({
    groupNames = [],
    actualRows = [],
    optimizedRows = [],
    sortKey,
    sortOrder,
}) => {
    const actualByGroup = indexGroupValues(actualRows);
    const optimizedByGroup = indexGroupValues(optimizedRows);
    const hasOptimized = optimizedByGroup.size > 0;
    const rows = groupNames.map((category) => Object.freeze({
        category,
        value: actualByGroup.get(category) ?? null,
        optValue: hasOptimized ? optimizedByGroup.get(category) ?? null : null,
    }));

    rows.sort((left, right) => {
        if (sortKey === 'category') {
            return withOrder(compareText(left.category, right.category), sortOrder);
        }
        const field = sortKey === 'optimized' ? 'optValue' : 'value';
        const comparison = compareNullable(left[field], right[field], compareMagnitude);
        if (comparison !== 0 && left[field] != null && right[field] != null) {
            return withOrder(comparison, sortOrder);
        }
        return comparison || compareText(left.category, right.category);
    });

    return Object.freeze({ rows: Object.freeze(rows), hasOptimized });
};

export const getChartExtent = (rows, includeOptimized) => {
    const values = rows.flatMap(({ value, optValue }) => (
        includeOptimized ? [value, optValue] : [value]
    )).filter(Number.isFinite);
    return {
        min: Math.min(...values, 0),
        max: Math.max(...values, 0),
    };
};
